import Inventory, { InventorySlot } from '../../inventory'
import {
  checkRecipes,
  CraftingMode,
  Recipe,
  recipes
} from '../../inventory/recipes'
import { BlockType } from '../../terrain'
import { blockIcons, blockNames } from '../../assets/block-icons'
import {
  getFoodValue,
  getMaxDurability,
  InventoryItemType,
  isBlockType,
  isBucket,
  isFoodItem,
  isTool,
  ItemType
} from '../../items'
import recipeBookIcon from '../../default-textures-master/textures/ui/recipe_book_icon.png'

type SlotRange = [number, number]
type CreativeCategoryId =
  | 'all'
  | 'terrain'
  | 'building'
  | 'tools'
  | 'food'
  | 'materials'

interface InventoryUIOptions {
  getCreativeItems?: () => InventoryItemType[] | null
  getCreativeHotbarItems?: () => InventoryItemType[] | null
  setCreativeHotbarItem?: (index: number, type: InventoryItemType) => void
}

const CREATIVE_SLOT_START = 1000
const CREATIVE_VISIBLE_SLOTS = 45

const CREATIVE_CATEGORIES: {
  id: CreativeCategoryId
  icon: InventoryItemType
}[] = [
  { id: 'all', icon: BlockType.craftingTable },
  { id: 'terrain', icon: BlockType.grass },
  { id: 'building', icon: BlockType.wood },
  { id: 'tools', icon: ItemType.stonePickaxe },
  { id: 'food', icon: ItemType.cookedBeef },
  { id: 'materials', icon: ItemType.gunpowder }
]

const categoryMatches = (
  category: CreativeCategoryId,
  type: InventoryItemType
): boolean => {
  if (category === 'all') return true
  if (category === 'terrain') {
    return (
      type === BlockType.grass ||
      type === BlockType.dirt ||
      type === BlockType.sand ||
      type === BlockType.tree ||
      type === BlockType.leaf ||
      type === BlockType.water ||
      type === BlockType.lava ||
      type === BlockType.netherrack ||
      type === BlockType.soulSand
    )
  }
  if (category === 'building') {
    return isBlockType(type) && !categoryMatches('terrain', type)
  }
  if (category === 'tools') {
    return isTool(type) || isBucket(type)
  }
  if (category === 'food') {
    return isFoodItem(type)
  }
  return !isBlockType(type) && !isTool(type) && !isBucket(type) && !isFoodItem(type)
}

export default class InventoryUI {
  constructor(inventory: Inventory, options: InventoryUIOptions = {}) {
    this.inventory = inventory
    this.options = options
    this.buildDOM()
  }

  inventory: Inventory
  options: InventoryUIOptions
  isOpen = false

  overlay = document.createElement('div')
  panel = document.createElement('div')
  title = document.createElement('div')
  feedback = document.createElement('div')
  craftingLabel = document.createElement('div')
  storageLabel = document.createElement('div')
  hotbarLabel = document.createElement('div')
  topLayout = document.createElement('div')
  equipmentColumn = document.createElement('div')
  playerPreview = document.createElement('div')
  offhandCell = document.createElement('div')
  craftingArea = document.createElement('div')
  craftingLine = document.createElement('div')
  recipeBookButton = document.createElement('button')
  creativeLayout = document.createElement('div')
  creativeTabs = document.createElement('div')
  creativeSearch: HTMLInputElement = document.createElement('input')
  creativeBrowser = document.createElement('div')
  creativeGrid = document.createElement('div')
  storageGrid = document.createElement('div')
  hotbarGrid = document.createElement('div')
  separator = document.createElement('div')
  cursorItem = document.createElement('div')
  tooltip = document.createElement('div')

  craftingContainer = document.createElement('div')
  craftingGrid = document.createElement('div')
  craftingArrow = document.createElement('div')
  craftingOutput = document.createElement('div')
  inventoryMain = document.createElement('div')
  recipePanel = document.createElement('div')
  recipeSearch: HTMLInputElement = document.createElement('input')
  recipeList = document.createElement('div')

  storageCells: HTMLDivElement[] = []
  hotbarCells: HTMLDivElement[] = []
  craftingCells: HTMLDivElement[] = []
  creativeCells: HTMLDivElement[] = []
  outputCell!: HTMLDivElement

  cursorStack: InventorySlot | null = null
  selectedRecipe: Recipe | null = null
  hoveredSlotIndex: number | null = null
  craftingMode: CraftingMode = 'personal'
  creativeCategory: CreativeCategoryId = 'all'
  creativeQuery = ''
  recipeQuery = ''
  recipeBookOpen = false
  lastPointerX = 0
  lastPointerY = 0
  feedbackTimer = 0

  buildDOM() {
    this.overlay.className = 'inventory-overlay hidden'
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.stashCursorStack()
      }
    })

    this.panel.className = 'inventory-panel'

    // recipe book opens as a side column to the left of the main inventory
    this.inventoryMain.className = 'inventory-main'

    this.title.className = 'inventory-title'
    this.title.textContent = 'Inventory'
    this.inventoryMain.appendChild(this.title)

    this.feedback.className = 'inventory-feedback hidden'
    this.inventoryMain.appendChild(this.feedback)

    this.creativeLayout.className = 'creative-layout hidden'
    this.creativeTabs.className = 'creative-tabs'
    for (const category of CREATIVE_CATEGORIES) {
      const tab = document.createElement('button')
      tab.type = 'button'
      tab.className = 'creative-tab'
      tab.dataset.category = category.id
      tab.addEventListener('click', event => {
        event.preventDefault()
        event.stopPropagation()
        this.creativeCategory = category.id
        this.refresh()
      })
      this.renderStack(tab, { type: category.icon, count: 1 }, 'creative-tab-icon')
      this.creativeTabs.appendChild(tab)
    }
    this.creativeSearch.className = 'creative-search'
    this.creativeSearch.type = 'search'
    this.creativeSearch.placeholder = 'Search Items'
    this.creativeSearch.autocomplete = 'off'
    this.creativeSearch.spellcheck = false
    this.creativeSearch.addEventListener('input', () => {
      this.creativeQuery = this.creativeSearch.value.trim().toLowerCase()
      this.refresh()
    })
    this.creativeSearch.addEventListener('mousedown', event => {
      event.stopPropagation()
    })
    this.creativeSearch.addEventListener('keydown', event => {
      if (event.key !== 'Escape') {
        event.stopPropagation()
      }
    })

    this.creativeBrowser.className = 'creative-browser'
    this.creativeGrid.className = 'inventory-grid creative-grid'
    for (let i = 0; i < CREATIVE_VISIBLE_SLOTS; i++) {
      const cell = this.createCell(CREATIVE_SLOT_START + i)
      cell.classList.add('creative-slot')
      this.creativeCells.push(cell)
      this.creativeGrid.appendChild(cell)
    }
    this.creativeBrowser.appendChild(this.creativeGrid)
    this.creativeLayout.appendChild(this.creativeTabs)
    this.creativeLayout.appendChild(this.creativeSearch)
    this.creativeLayout.appendChild(this.creativeBrowser)
    this.inventoryMain.appendChild(this.creativeLayout)

    this.topLayout.className = 'inventory-top'
    this.equipmentColumn.className = 'equipment-column'
    this.equipmentColumn.appendChild(this.createEquipmentCell('helmet'))
    this.equipmentColumn.appendChild(this.createEquipmentCell('chestplate'))
    this.equipmentColumn.appendChild(this.createEquipmentCell('leggings'))
    this.equipmentColumn.appendChild(this.createEquipmentCell('boots'))

    this.playerPreview.className = 'player-preview'
    this.buildPlayerPreview()
    this.offhandCell.className = 'inv-cell equipment-slot offhand-slot'

    this.craftingLabel.className = 'inventory-section-label'
    this.craftingLabel.textContent = 'Crafting'
    this.craftingArea.className = 'crafting-area'
    this.craftingArea.appendChild(this.craftingLabel)

    this.craftingContainer.className = 'inventory-crafting-container'

    this.craftingGrid.className = 'inventory-grid crafting-grid'
    for (let i = Inventory.CRAFTING_START; i <= Inventory.CRAFTING_END; i++) {
      const cell = this.createCell(i)
      this.craftingCells.push(cell)
      this.craftingGrid.appendChild(cell)
    }

    this.craftingArrow.className = 'crafting-arrow'
    this.craftingArrow.textContent = '->'

    this.craftingOutput.className = 'crafting-output'
    this.outputCell = this.createCell(Inventory.CRAFTING_OUTPUT)
    this.outputCell.classList.add('output-cell')
    this.craftingOutput.appendChild(this.outputCell)

    this.recipeBookButton.type = 'button'
    this.recipeBookButton.className = 'recipe-book-button'
    const recipeBookImage = document.createElement('img')
    recipeBookImage.src = recipeBookIcon
    recipeBookImage.alt = ''
    recipeBookImage.draggable = false
    this.recipeBookButton.appendChild(recipeBookImage)
    this.recipeBookButton.addEventListener('click', event => {
      event.preventDefault()
      event.stopPropagation()
      this.recipeBookOpen = !this.recipeBookOpen
      this.renderRecipeBook()
    })

    this.craftingContainer.appendChild(this.craftingGrid)
    this.craftingContainer.appendChild(this.craftingArrow)
    this.craftingContainer.appendChild(this.craftingOutput)

    this.craftingLine.className = 'crafting-line'
    this.craftingLine.appendChild(this.recipeBookButton)
    this.craftingLine.appendChild(this.craftingContainer)
    this.craftingArea.appendChild(this.craftingLine)

    this.topLayout.appendChild(this.equipmentColumn)
    this.topLayout.appendChild(this.playerPreview)
    this.topLayout.appendChild(this.offhandCell)
    this.topLayout.appendChild(this.craftingArea)
    this.inventoryMain.appendChild(this.topLayout)

    this.recipePanel.className = 'recipe-panel'
    const recipeTitle = document.createElement('div')
    recipeTitle.className = 'recipe-title'
    recipeTitle.textContent = 'Recipes'
    this.recipeSearch.className = 'recipe-search'
    this.recipeSearch.type = 'search'
    this.recipeSearch.placeholder = 'Search Recipes'
    this.recipeSearch.autocomplete = 'off'
    this.recipeSearch.spellcheck = false
    this.recipeSearch.addEventListener('input', () => {
      this.recipeQuery = this.recipeSearch.value.trim().toLowerCase()
      this.renderRecipeBook()
    })
    this.recipeSearch.addEventListener('mousedown', event => {
      event.stopPropagation()
    })
    this.recipeSearch.addEventListener('keydown', event => {
      if (event.key !== 'Escape') {
        event.stopPropagation()
      }
    })
    this.recipeList.className = 'recipe-list'
    this.recipePanel.appendChild(recipeTitle)
    this.recipePanel.appendChild(this.recipeSearch)
    this.recipePanel.appendChild(this.recipeList)
    this.panel.appendChild(this.recipePanel)
    this.panel.appendChild(this.inventoryMain)

    this.storageLabel.className = 'inventory-section-label'
    this.storageLabel.textContent = 'Storage'
    this.inventoryMain.appendChild(this.storageLabel)

    this.storageGrid.className = 'inventory-grid storage-grid'
    for (let i = 0; i < 27; i++) {
      const cell = this.createCell(i + 9)
      this.storageCells.push(cell)
      this.storageGrid.appendChild(cell)
    }
    this.inventoryMain.appendChild(this.storageGrid)

    this.separator.className = 'inventory-separator'
    this.inventoryMain.appendChild(this.separator)

    this.hotbarLabel.className = 'inventory-section-label'
    this.hotbarLabel.textContent = 'Hotbar'
    this.inventoryMain.appendChild(this.hotbarLabel)

    this.hotbarGrid.className = 'inventory-grid hotbar-grid'
    for (let i = 0; i < 9; i++) {
      const cell = this.createCell(i)
      this.hotbarCells.push(cell)
      this.hotbarGrid.appendChild(cell)
    }
    this.inventoryMain.appendChild(this.hotbarGrid)

    this.overlay.appendChild(this.panel)
    document.body.appendChild(this.overlay)

    this.cursorItem.className = 'inventory-cursor hidden'
    document.body.appendChild(this.cursorItem)
    this.tooltip.className = 'inventory-tooltip hidden'
    document.body.appendChild(this.tooltip)
    document.addEventListener('mousemove', this.moveCursor)
    document.addEventListener('keydown', this.handleDocumentKeyDown, true)
  }

  createEquipmentCell(kind: string) {
    const cell = document.createElement('div')
    cell.className = `inv-cell equipment-slot equipment-slot--${kind}`
    return cell
  }

  buildPlayerPreview() {
    const avatar = document.createElement('div')
    avatar.className = 'inventory-player-avatar'
    const parts = [
      'head',
      'body',
      'arm-left',
      'arm-right',
      'leg-left',
      'leg-right'
    ]
    for (const part of parts) {
      const piece = document.createElement('div')
      piece.className = `avatar-part avatar-${part}`
      avatar.appendChild(piece)
    }
    this.playerPreview.appendChild(avatar)
  }

  createCell(slotIndex: number): HTMLDivElement {
    const cell = document.createElement('div')
    const kind =
      slotIndex < Inventory.HOTBAR_SIZE
        ? 'hotbar-slot'
        : slotIndex <= 35
          ? 'storage-slot'
          : slotIndex === Inventory.CRAFTING_OUTPUT
            ? 'output-slot'
            : 'crafting-slot'
    cell.className = `inv-cell ${kind}`
    cell.dataset.slot = String(slotIndex)

    cell.addEventListener('mousedown', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.handleCellClick(slotIndex, e)
    })

    cell.addEventListener('contextmenu', (e) => {
      e.preventDefault()
      e.stopPropagation()
    })

    cell.addEventListener('mouseenter', (e) => {
      this.hoveredSlotIndex = slotIndex
      this.moveCursor(e)
      this.refreshHoveredCells()
      this.updateTooltip()
    })

    cell.addEventListener('mousemove', (e) => {
      this.hoveredSlotIndex = slotIndex
      this.moveCursor(e)
      this.updateTooltip()
    })

    cell.addEventListener('mouseleave', () => {
      if (this.hoveredSlotIndex === slotIndex) {
        this.hoveredSlotIndex = null
        this.refreshHoveredCells()
        this.updateTooltip()
      }
    })

    return cell
  }

  handleCellClick(slotIndex: number, event: MouseEvent) {
    this.moveCursor(event)
    this.hoveredSlotIndex = slotIndex

    if (this.handleCreativeClick(slotIndex, event)) {
      this.refresh()
      return
    }

    if (event.button === 0 && event.detail >= 2 && !event.shiftKey) {
      this.collectMatchingToCursor(slotIndex)
    } else if (event.shiftKey) {
      this.handleShiftClick(slotIndex)
    } else if (slotIndex === Inventory.CRAFTING_OUTPUT) {
      this.takeCraftingOutput()
    } else if (event.button === 2) {
      this.handleRightClick(slotIndex)
    } else {
      this.handleLeftClick(slotIndex)
    }

    this.resolveRecipes()
    this.refresh()
  }

  handleCreativeClick(slotIndex: number, event: MouseEvent) {
    const creativeItems = this.options.getCreativeItems?.() ?? null
    if (!creativeItems) return false

    if (this.isCreativeBrowserSlot(slotIndex)) {
      const type = this.getCreativeVisibleItems()[slotIndex - CREATIVE_SLOT_START]
      if (type === undefined) return true

      this.cursorStack = {
        type,
        count: event.button === 2 ? 1 : this.getStackLimit(type)
      }
      return true
    }

    if (slotIndex >= 0 && slotIndex < Inventory.HOTBAR_SIZE) {
      const hotbar = this.options.getCreativeHotbarItems?.() ?? null
      const hotbarType = hotbar?.[slotIndex]

      if (this.cursorStack) {
        this.options.setCreativeHotbarItem?.(slotIndex, this.cursorStack.type)
      } else if (hotbarType !== undefined) {
        this.cursorStack = {
          type: hotbarType,
          count: event.button === 2 ? 1 : this.getStackLimit(hotbarType)
        }
      }

      return true
    }

    return false
  }

  handleDocumentKeyDown = (event: KeyboardEvent) => {
    if (!this.isOpen) return

    const numberKey = Number(event.key)
    if (
      Number.isInteger(numberKey) &&
      numberKey >= 1 &&
      numberKey <= Inventory.HOTBAR_SIZE &&
      this.hoveredSlotIndex !== null
    ) {
      event.preventDefault()
      event.stopImmediatePropagation()
      this.handleHotbarNumber(this.hoveredSlotIndex, numberKey - 1)
    }
  }

  handleHotbarNumber(slotIndex: number, hotbarIndex: number) {
    if (slotIndex === Inventory.CRAFTING_OUTPUT || slotIndex === hotbarIndex) {
      return
    }

    const creativeItems = this.options.getCreativeItems?.() ?? null
    if (creativeItems) {
      if (this.isCreativeBrowserSlot(slotIndex)) {
        const type = this.getCreativeVisibleItems()[slotIndex - CREATIVE_SLOT_START]
        if (type !== undefined) {
          this.options.setCreativeHotbarItem?.(hotbarIndex, type)
          this.showFeedback(`Hotbar ${hotbarIndex + 1}: ${blockNames[type]}`)
          this.refresh()
        }
      }
      return
    }

    this.inventory.swapSlots(slotIndex, hotbarIndex)
    this.resolveRecipes()
    this.showFeedback(`Hotbar ${hotbarIndex + 1} updated`)
    this.refresh()
  }

  showFeedback(message: string) {
    window.clearTimeout(this.feedbackTimer)
    this.feedback.textContent = message
    this.feedback.classList.remove('hidden')
    this.feedbackTimer = window.setTimeout(() => {
      this.feedback.classList.add('hidden')
    }, 1300)
  }

  handleLeftClick(slotIndex: number) {
    const slot = this.inventory.getSlot(slotIndex)

    if (!this.cursorStack) {
      if (!slot) return
      this.cursorStack = { ...slot }
      this.inventory.slots[slotIndex] = null
      return
    }

    if (!slot) {
      this.inventory.slots[slotIndex] = { ...this.cursorStack }
      this.cursorStack = null
      return
    }

    if (slot.type === this.cursorStack.type) {
      const moved = this.mergeIntoSlot(slot, this.cursorStack.count)
      this.cursorStack.count -= moved
      this.normalizeCursor()
      return
    }

    this.inventory.slots[slotIndex] = { ...this.cursorStack }
    this.cursorStack = { ...slot }
  }

  handleRightClick(slotIndex: number) {
    const slot = this.inventory.getSlot(slotIndex)
    const stackLimit = this.cursorStack
      ? this.getStackLimit(this.cursorStack.type)
      : Inventory.MAX_STACK

    if (!this.cursorStack) {
      if (!slot) return
      const taken = Math.ceil(slot.count / 2)
      this.cursorStack = { type: slot.type, count: taken }
      slot.count -= taken
      this.normalizeSlot(slotIndex)
      return
    }

    if (!slot) {
      this.inventory.slots[slotIndex] = {
        type: this.cursorStack.type,
        count: 1
      }
      this.cursorStack.count--
      this.normalizeCursor()
      return
    }

    if (slot.type === this.cursorStack.type && slot.count < stackLimit) {
      slot.count++
      this.cursorStack.count--
      this.normalizeCursor()
      return
    }

    this.inventory.slots[slotIndex] = { ...this.cursorStack }
    this.cursorStack = { ...slot }
  }

  collectMatchingToCursor(slotIndex: number) {
    const targetStack = this.cursorStack ?? this.inventory.getSlot(slotIndex)
    if (!targetStack || slotIndex === Inventory.CRAFTING_OUTPUT) return

    if (!this.cursorStack) {
      this.cursorStack = { ...targetStack }
      this.inventory.slots[slotIndex] = null
    }

    const stackLimit = this.getStackLimit(this.cursorStack.type)
    if (this.cursorStack.count >= stackLimit) return

    for (let i = 0; i < Inventory.CRAFTING_OUTPUT; i++) {
      if (this.cursorStack.count >= stackLimit) break
      const slot = this.inventory.getSlot(i)
      if (!slot || slot.type !== this.cursorStack.type) continue

      const moved = Math.min(slot.count, stackLimit - this.cursorStack.count)
      slot.count -= moved
      this.cursorStack.count += moved
      this.normalizeSlot(i)
    }
  }

  handleShiftClick(slotIndex: number) {
    if (slotIndex === Inventory.CRAFTING_OUTPUT) {
      this.shiftCraftingOutput()
      return
    }

    const slot = this.inventory.getSlot(slotIndex)
    if (!slot) return

    const ranges = this.getTransferRanges(slotIndex)
    const remaining = this.addStackToRanges({ ...slot }, ranges)
    this.inventory.slots[slotIndex] =
      remaining > 0 ? { type: slot.type, count: remaining } : null
  }

  takeCraftingOutput() {
    const recipe = this.resolveRecipes()
    if (!recipe || !this.canCursorAccept(recipe)) return

    if (!this.cursorStack) {
      this.cursorStack = this.createResultStack(recipe.resultType, recipe.resultCount)
    } else {
      this.cursorStack.count += recipe.resultCount
    }

    this.consumeCraftingIngredients()
  }

  shiftCraftingOutput() {
    let recipe = this.resolveRecipes()
    while (recipe && this.canAddStackToRanges(recipe, [[0, 35]])) {
      this.addStackToRanges(
        this.createResultStack(recipe.resultType, recipe.resultCount),
        [[9, 35], [0, 8]]
      )
      this.consumeCraftingIngredients()
      recipe = this.resolveRecipes()
    }
  }

  createResultStack(type: InventorySlot['type'], count: number): InventorySlot {
    const stack: InventorySlot = { type, count }
    if (isTool(type)) stack.durability = getMaxDurability(type)
    return stack
  }

  getStackLimit(type: InventorySlot['type']) {
    return isTool(type) ? 1 : Inventory.MAX_STACK
  }

  canCursorAccept(recipe: Recipe) {
    if (!this.cursorStack) return true
    const stackLimit = this.getStackLimit(recipe.resultType)
    return (
      this.cursorStack.type === recipe.resultType &&
      this.cursorStack.count + recipe.resultCount <= stackLimit
    )
  }

  consumeCraftingIngredients() {
    for (let i = Inventory.CRAFTING_START; i <= this.activeCraftingEnd(); i++) {
      if (this.inventory.getSlot(i)) {
        this.inventory.removeItem(i, 1)
      }
    }
  }

  resolveRecipes(): Recipe | null {
    if (this.options.getCreativeItems?.()) {
      this.inventory.slots[Inventory.CRAFTING_OUTPUT] = null
      return null
    }

    const craftingSlots: (InventorySlot | null)[] = []
    for (let i = Inventory.CRAFTING_START; i <= this.activeCraftingEnd(); i++) {
      craftingSlots.push(this.inventory.getSlot(i))
    }
    const recipe = checkRecipes(craftingSlots, this.craftingMode)

    if (recipe) {
      this.inventory.slots[Inventory.CRAFTING_OUTPUT] = this.createResultStack(
        recipe.resultType,
        recipe.resultCount
      )
    } else {
      this.inventory.slots[Inventory.CRAFTING_OUTPUT] = null
    }

    return recipe
  }

  activeCraftingEnd() {
    return this.craftingMode === 'table'
      ? Inventory.CRAFTING_END
      : Inventory.PERSONAL_CRAFTING_END
  }

  getTransferRanges(slotIndex: number): SlotRange[] {
    if (slotIndex < Inventory.HOTBAR_SIZE) {
      return [[9, 35]]
    }

    if (slotIndex <= 35) {
      return [[0, 8]]
    }

    return [[9, 35], [0, 8]]
  }

  addStackToRanges(stack: InventorySlot, ranges: SlotRange[]): number {
    let remaining = stack.count

    for (const [start, end] of ranges) {
      for (let i = start; i <= end && remaining > 0; i++) {
        const slot = this.inventory.getSlot(i)
        if (
          slot &&
          slot.type === stack.type &&
          slot.count < this.getStackLimit(stack.type)
        ) {
          const moved = this.mergeIntoSlot(slot, remaining)
          remaining -= moved
        }
      }
    }

    for (const [start, end] of ranges) {
      for (let i = start; i <= end && remaining > 0; i++) {
        if (!this.inventory.getSlot(i)) {
          const moved = Math.min(remaining, this.getStackLimit(stack.type))
          this.inventory.slots[i] = { ...stack, count: moved }
          remaining -= moved
        }
      }
    }

    return remaining
  }

  canAddStackToRanges(recipe: Recipe, ranges: SlotRange[]): boolean {
    let remaining = recipe.resultCount
    const stackLimit = this.getStackLimit(recipe.resultType)
    for (const [start, end] of ranges) {
      for (let i = start; i <= end && remaining > 0; i++) {
        const slot = this.inventory.getSlot(i)
        if (!slot) {
          remaining -= stackLimit
        } else if (slot.type === recipe.resultType) {
          remaining -= stackLimit - slot.count
        }
      }
    }
    return remaining <= 0
  }

  mergeIntoSlot(slot: InventorySlot, count: number): number {
    const moved = Math.min(count, this.getStackLimit(slot.type) - slot.count)
    slot.count += moved
    return moved
  }

  normalizeSlot(slotIndex: number) {
    const slot = this.inventory.getSlot(slotIndex)
    if (slot && slot.count <= 0) {
      this.inventory.slots[slotIndex] = null
    }
  }

  normalizeCursor() {
    if (this.cursorStack && this.cursorStack.count <= 0) {
      this.cursorStack = null
    }
  }

  stashCursorStack() {
    if (!this.cursorStack) return

    if (this.options.getCreativeItems?.()) {
      this.cursorStack = null
      this.refresh()
      return
    }

    const remaining = this.addStackToRanges({ ...this.cursorStack }, [[0, 35]])
    this.cursorStack = remaining > 0
      ? { ...this.cursorStack, count: remaining }
      : null
    this.refresh()
  }

  stashCraftingInputs() {
    if (this.options.getCreativeItems?.()) {
      this.inventory.slots[Inventory.CRAFTING_OUTPUT] = null
      return
    }

    this.inventory.slots[Inventory.CRAFTING_OUTPUT] = null
    for (let i = Inventory.CRAFTING_START; i <= Inventory.CRAFTING_END; i++) {
      const slot = this.inventory.getSlot(i)
      if (!slot) continue

      const remaining = this.addStackToRanges({ ...slot }, [[0, 35]])
      this.inventory.slots[i] =
        remaining > 0 ? { ...slot, count: remaining } : null
    }
  }

  getRecipeKey(recipe: Recipe) {
    const counts = new Map<number, number>()
    for (const type of recipe.pattern) {
      if (type === null) continue
      counts.set(type, (counts.get(type) ?? 0) + 1)
    }

    const ingredients = [...counts.entries()]
      .sort(([a], [b]) => a - b)
      .map(([type, count]) => `${type}:${count}`)
      .join(',')

    return `${recipe.mode}|${recipe.resultType}|${recipe.resultCount}|${ingredients}`
  }

  getVisibleRecipes() {
    const seen = new Set<string>()
    const query = this.recipeQuery
    return recipes.filter(recipe => {
      if (recipe.mode !== this.craftingMode) return false
      if (
        query &&
        !(blockNames[recipe.resultType] || '').toLowerCase().includes(query)
      ) {
        return false
      }

      const key = this.getRecipeKey(recipe)
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }

  getAvailableItemCounts() {
    const counts = new Map<number, number>()

    for (let i = 0; i < Inventory.CRAFTING_OUTPUT; i++) {
      const slot = this.inventory.getSlot(i)
      if (!slot) continue
      counts.set(slot.type, (counts.get(slot.type) ?? 0) + slot.count)
    }

    if (this.cursorStack) {
      counts.set(
        this.cursorStack.type,
        (counts.get(this.cursorStack.type) ?? 0) + this.cursorStack.count
      )
    }

    return counts
  }

  canCraftRecipe(recipe: Recipe) {
    const available = this.getAvailableItemCounts()
    const needed = new Map<number, number>()

    for (const type of recipe.pattern) {
      if (type === null) continue
      needed.set(type, (needed.get(type) ?? 0) + 1)
    }

    for (const [type, count] of needed) {
      if ((available.get(type) ?? 0) < count) return false
    }

    return true
  }

  renderRecipeBook() {
    const creative = Boolean(this.options.getCreativeItems?.())
    const bookVisible = !creative && this.recipeBookOpen
    this.recipePanel.classList.toggle('hidden', !bookVisible)
    this.panel.classList.toggle('inventory-panel--book-open', bookVisible)
    this.recipeBookButton.classList.toggle(
      'recipe-book-button--open',
      this.recipeBookOpen
    )
    if (!bookVisible) return

    const visibleRecipes = this.getVisibleRecipes()
    if (
      this.selectedRecipe &&
      (this.selectedRecipe.mode !== this.craftingMode ||
        !visibleRecipes.some(
          recipe =>
            this.getRecipeKey(recipe) === this.getRecipeKey(this.selectedRecipe!)
        ))
    ) {
      this.selectedRecipe = null
    }

    this.recipeList.innerHTML = ''
    for (const recipe of visibleRecipes) {
      const card = document.createElement('button')
      card.type = 'button'
      card.className = 'recipe-card'
      card.classList.toggle('recipe-craftable', this.canCraftRecipe(recipe))
      card.classList.toggle(
        'recipe-selected',
        this.selectedRecipe !== null &&
          this.getRecipeKey(recipe) === this.getRecipeKey(this.selectedRecipe)
      )
      this.renderStack(
        card,
        this.createResultStack(recipe.resultType, recipe.resultCount),
        'recipe-icon',
        'recipe-count'
      )

      const name = document.createElement('span')
      name.className = 'recipe-name'
      name.textContent = blockNames[recipe.resultType]
      card.appendChild(name)

      card.addEventListener('click', event => {
        event.preventDefault()
        event.stopPropagation()
        this.fillRecipeIntoGrid(recipe)
      })
      card.addEventListener('mouseenter', event => {
        this.moveCursor(event)
        this.renderTooltipForStack(
          this.createResultStack(recipe.resultType, recipe.resultCount)
        )
      })
      card.addEventListener('mousemove', event => {
        this.moveCursor(event)
        this.renderTooltipForStack(
          this.createResultStack(recipe.resultType, recipe.resultCount)
        )
      })
      card.addEventListener('mouseleave', () => {
        this.updateTooltip()
      })

      this.recipeList.appendChild(card)
    }
  }

  // fast crafting: clicking a recipe pulls its ingredients out of the main
  // inventory into the crafting grid, so the result instantly appears in the
  // output slot. Clicking again queues another batch on top.
  fillRecipeIntoGrid(recipe: Recipe) {
    const gridStart = Inventory.CRAFTING_START
    const size = this.craftingMode === 'table' ? 9 : 4

    // does the grid already hold exactly this recipe's layout?
    let gridEmpty = true
    let matchesCurrent = true
    for (let i = 0; i < size; i++) {
      const slot = this.inventory.getSlot(gridStart + i)
      if (slot) gridEmpty = false
      if ((slot ? slot.type : null) !== recipe.pattern[i]) {
        matchesCurrent = false
      }
    }

    if (!gridEmpty && !matchesCurrent) {
      this.stashCraftingInputs()
      // a full inventory can leave leftovers in the grid — bail out cleanly
      for (let i = gridStart; i <= Inventory.CRAFTING_END; i++) {
        if (this.inventory.getSlot(i)) {
          this.showFeedback('Inventory is full')
          this.refresh()
          return
        }
      }
    }

    // one batch needs 1 item per occupied pattern cell
    const needed = new Map<InventoryItemType, number>()
    for (const type of recipe.pattern) {
      if (type === null) continue
      needed.set(type, (needed.get(type) ?? 0) + 1)
    }

    // only the main inventory (hotbar + storage) feeds the grid
    const available = new Map<InventoryItemType, number>()
    for (let i = 0; i <= 35; i++) {
      const slot = this.inventory.getSlot(i)
      if (!slot) continue
      available.set(slot.type, (available.get(slot.type) ?? 0) + slot.count)
    }
    for (const [type, count] of needed) {
      if ((available.get(type) ?? 0) < count) {
        this.showFeedback('Missing materials')
        this.refresh()
        return
      }
    }

    // batching cap: every occupied cell must have room for one more
    if (!gridEmpty && matchesCurrent) {
      for (let i = 0; i < size; i++) {
        const slot = this.inventory.getSlot(gridStart + i)
        if (slot && slot.count >= this.getStackLimit(slot.type)) {
          this.showFeedback('Crafting grid is full')
          return
        }
      }
    }

    const takeOneFromInventory = (type: InventoryItemType) => {
      const ranges: SlotRange[] = [[9, 35], [0, 8]]
      for (const [start, end] of ranges) {
        for (let i = start; i <= end; i++) {
          const slot = this.inventory.getSlot(i)
          if (slot && slot.type === type) {
            this.inventory.removeItem(i, 1)
            return true
          }
        }
      }
      return false
    }

    for (let i = 0; i < size; i++) {
      const type = recipe.pattern[i]
      if (type === null) continue
      if (!takeOneFromInventory(type)) continue
      const slot = this.inventory.getSlot(gridStart + i)
      if (slot && slot.type === type) {
        slot.count++
      } else {
        this.inventory.slots[gridStart + i] = { type, count: 1 }
      }
    }

    this.selectedRecipe = recipe
    this.resolveRecipes()
    this.refresh()
  }

  moveCursor = (event: MouseEvent) => {
    this.lastPointerX = event.clientX
    this.lastPointerY = event.clientY
    this.cursorItem.style.left = `${this.lastPointerX}px`
    this.cursorItem.style.top = `${this.lastPointerY}px`
    this.positionTooltip()
  }

  positionTooltip() {
    if (this.tooltip.classList.contains('hidden')) return

    const width = 230
    const left = Math.min(this.lastPointerX + 16, window.innerWidth - width - 8)
    const top = Math.min(this.lastPointerY + 18, window.innerHeight - 112)
    this.tooltip.style.left = `${Math.max(8, left)}px`
    this.tooltip.style.top = `${Math.max(8, top)}px`
  }

  updateTooltip() {
    if (!this.isOpen || this.hoveredSlotIndex === null) {
      this.tooltip.classList.add('hidden')
      return
    }

    const stack = this.getDisplaySlot(this.hoveredSlotIndex)
    if (!stack) {
      this.tooltip.classList.add('hidden')
      return
    }

    this.renderTooltipForStack(stack)
  }

  renderTooltipForStack(stack: InventorySlot) {
    this.tooltip.innerHTML = ''

    const title = document.createElement('div')
    title.className = 'inventory-tooltip-title'
    title.textContent = blockNames[stack.type] || 'Unknown Item'
    this.tooltip.appendChild(title)

    for (const line of this.getTooltipLines(stack)) {
      const row = document.createElement('div')
      row.className = 'inventory-tooltip-line'
      row.textContent = line
      this.tooltip.appendChild(row)
    }

    this.tooltip.classList.remove('hidden')
    this.positionTooltip()
  }

  getTooltipLines(stack: InventorySlot) {
    const lines = [`Count ${stack.count}/${this.getStackLimit(stack.type)}`]
    const durability = getMaxDurability(stack.type)

    if (durability > 0) {
      lines.push(`Durability ${stack.durability ?? durability}/${durability}`)
    } else if (isFoodItem(stack.type)) {
      lines.push(`Food +${getFoodValue(stack.type)}`)
    } else if (isBlockType(stack.type)) {
      lines.push('Placeable block')
    } else {
      lines.push('Crafting material')
    }

    return lines
  }

  renderStack(
    container: HTMLElement,
    stack: InventorySlot | null,
    iconClass = 'inv-icon',
    countClass = 'inv-count'
  ) {
    container.innerHTML = ''
    if (!stack) return

    const icon = blockIcons[stack.type]
    if (icon) {
      const img = document.createElement('img')
      img.src = icon
      img.className = iconClass
      img.alt = blockNames[stack.type] || 'Block'
      img.draggable = false
      container.appendChild(img)
    } else {
      const fallback = document.createElement('div')
      fallback.className = 'inv-fallback-icon'
      fallback.textContent = (blockNames[stack.type] || '?').charAt(0)
      container.appendChild(fallback)
    }

    if (stack.count > 1) {
      const badge = document.createElement('span')
      badge.className = countClass
      badge.textContent = String(stack.count)
      container.appendChild(badge)
    }

    const max = getMaxDurability(stack.type)
    if (max > 0 && stack.durability !== undefined && stack.durability < max) {
      const bar = document.createElement('div')
      bar.className = 'durability-bar'
      const fill = document.createElement('div')
      fill.className = 'durability-fill'
      const ratio = Math.max(0, stack.durability / max)
      fill.style.width = `${ratio * 100}%`
      const hue = Math.round(120 * ratio)
      fill.style.background = `hsl(${hue}, 90%, 50%)`
      bar.appendChild(fill)
      container.appendChild(bar)
    }
  }

  refreshCursor() {
    this.renderStack(
      this.cursorItem,
      this.cursorStack,
      'inv-icon cursor-icon',
      'inv-count cursor-count'
    )
    this.cursorItem.classList.toggle('hidden', !this.cursorStack || !this.isOpen)
  }

  refresh() {
    this.resolveRecipes()

    for (let i = 0; i < 27; i++) {
      this.updateCell(this.storageCells[i], i + 9)
    }
    for (let i = 0; i < 9; i++) {
      this.updateCell(this.hotbarCells[i], i)
    }
    for (let i = 0; i < this.craftingCells.length; i++) {
      this.updateCell(this.craftingCells[i], i + Inventory.CRAFTING_START)
    }
    for (let i = 0; i < this.creativeCells.length; i++) {
      this.updateCell(this.creativeCells[i], CREATIVE_SLOT_START + i)
    }
    this.updateCell(this.outputCell, Inventory.CRAFTING_OUTPUT)
    this.refreshCreativeTabs()
    this.renderRecipeBook()
    this.refreshCursor()
    this.updateTooltip()
  }

  updateCell(cell: HTMLDivElement, slotIndex: number) {
    const stack = this.getDisplaySlot(slotIndex)
    this.renderStack(cell, stack)
    cell.classList.toggle('inv-filled', Boolean(stack))
    cell.classList.toggle('inv-hovered', this.hoveredSlotIndex === slotIndex)
    cell.classList.toggle(
      'output-ready',
      slotIndex === Inventory.CRAFTING_OUTPUT && Boolean(stack)
    )

    if (slotIndex >= 0 && slotIndex < Inventory.HOTBAR_SIZE) {
      const badge = document.createElement('span')
      badge.className = 'hotbar-index'
      badge.textContent = String(slotIndex + 1)
      cell.appendChild(badge)
    }
  }

  refreshHoveredCells() {
    for (let i = 0; i < this.storageCells.length; i++) {
      this.storageCells[i].classList.toggle(
        'inv-hovered',
        this.hoveredSlotIndex === i + 9
      )
    }
    for (let i = 0; i < this.hotbarCells.length; i++) {
      this.hotbarCells[i].classList.toggle(
        'inv-hovered',
        this.hoveredSlotIndex === i
      )
    }
    for (let i = 0; i < this.craftingCells.length; i++) {
      this.craftingCells[i].classList.toggle(
        'inv-hovered',
        this.hoveredSlotIndex === i + Inventory.CRAFTING_START
      )
    }
    this.outputCell.classList.toggle(
      'inv-hovered',
      this.hoveredSlotIndex === Inventory.CRAFTING_OUTPUT
    )
    for (let i = 0; i < this.creativeCells.length; i++) {
      this.creativeCells[i].classList.toggle(
        'inv-hovered',
        this.hoveredSlotIndex === CREATIVE_SLOT_START + i
      )
    }
  }

  isCreativeBrowserSlot(slotIndex: number) {
    return (
      slotIndex >= CREATIVE_SLOT_START &&
      slotIndex < CREATIVE_SLOT_START + CREATIVE_VISIBLE_SLOTS
    )
  }

  getCreativeVisibleItems() {
    const items = this.options.getCreativeItems?.() ?? []
    const query = this.creativeQuery
    return items.filter(type => {
      if (!categoryMatches(this.creativeCategory, type)) return false
      if (!query) return true
      return (blockNames[type] || '').toLowerCase().includes(query)
    })
  }

  refreshCreativeTabs() {
    for (const tab of Array.from(this.creativeTabs.children)) {
      if (!(tab instanceof HTMLElement)) continue
      tab.classList.toggle(
        'creative-tab--active',
        tab.dataset.category === this.creativeCategory
      )
    }
  }

  getDisplaySlot(slotIndex: number): InventorySlot | null {
    const creativeItems = this.options.getCreativeItems?.() ?? null
    if (!creativeItems) {
      return this.inventory.getSlot(slotIndex)
    }

    if (this.isCreativeBrowserSlot(slotIndex)) {
      const type = this.getCreativeVisibleItems()[slotIndex - CREATIVE_SLOT_START]
      return type === undefined ? null : { type, count: 1 }
    }

    if (slotIndex >= 0 && slotIndex < Inventory.HOTBAR_SIZE) {
      const type = this.options.getCreativeHotbarItems?.()?.[slotIndex]
      return type === undefined ? null : { type, count: 1 }
    }

    return null
  }

  setCraftingMode(mode: CraftingMode) {
    this.craftingMode = mode
    if (this.selectedRecipe?.mode !== mode) {
      this.selectedRecipe = null
    }
    this.craftingGrid.classList.toggle('crafting-grid--table', mode === 'table')
    this.craftingCells.forEach((cell, index) => {
      cell.classList.toggle('hidden', mode === 'personal' && index >= 4)
    })
  }

  open(mode: CraftingMode = 'personal') {
    this.isOpen = true
    const creative = Boolean(this.options.getCreativeItems?.())
    this.setCraftingMode(mode)
    this.recipeBookOpen = false
    this.title.textContent = creative
      ? 'Creative Inventory'
      : mode === 'table'
        ? 'Crafting Table'
        : 'Inventory'
    this.panel.classList.toggle('inventory-panel--creative', creative)
    this.panel.classList.toggle(
      'inventory-panel--survival',
      !creative && mode === 'personal'
    )
    this.panel.classList.toggle(
      'inventory-panel--table',
      !creative && mode === 'table'
    )
    this.topLayout.classList.toggle('hidden', creative)
    this.creativeLayout.classList.toggle('hidden', !creative)
    this.equipmentColumn.classList.toggle('hidden', mode !== 'personal')
    this.playerPreview.classList.toggle('hidden', mode !== 'personal')
    this.offhandCell.classList.toggle('hidden', mode !== 'personal')
    this.craftingLabel.classList.toggle('hidden', creative)
    this.craftingContainer.classList.toggle('hidden', creative)
    this.recipeBookButton.classList.toggle('hidden', creative)
    this.recipePanel.classList.add('hidden')
    this.panel.classList.remove('inventory-panel--book-open')
    this.recipeQuery = ''
    this.recipeSearch.value = ''
    this.storageLabel.classList.toggle('hidden', creative)
    this.storageGrid.classList.toggle('hidden', creative)
    this.storageLabel.textContent = 'Inventory'
    this.hotbarLabel.classList.add('hidden')
    this.refresh()
    this.overlay.classList.remove('hidden')
    requestAnimationFrame(() => {
      this.overlay.classList.add('inv-visible')
    })
  }

  close() {
    this.stashCursorStack()
    this.stashCraftingInputs()
    this.isOpen = false
    this.hoveredSlotIndex = null
    this.overlay.classList.remove('inv-visible')
    this.overlay.classList.add('hidden')
    this.refreshCursor()
    this.updateTooltip()
  }

  toggle() {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }
}
